import Big from 'big.js';
import { DateTime } from 'luxon';
import { Format } from 'smg/address';
import axios from 'smg/axios';
import * as buttons from 'smg/buttons';
import * as session from 'smg/customer-api/session';
import { Escape, Nl2br } from 'smg/html';
import * as models from 'smg/models';
import { G } from 'smg/querystring';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';
import * as payments from '../payments';
import { DrawPaymentOption } from '../payments';

declare interface orderResp {
    order: models.Order;
}

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

export default async function main() {
    if (await session.Redirect(undefined, Relref('/login'))) {
        return;
    }

    const sess = await session.Session().catch(() => undefined);

    const spinner = document.querySelector('#spinner');

    function showNotFound() {
        spinner?.classList.add('d-none');
        const notFound = document.querySelector('#not-found');

        notFound?.classList.remove('d-none');
    }

    const tableContainer = document.querySelector('#order-table') as HTMLElement;
    const invoiceNumberEl = document.querySelector('#invoice-number') as HTMLElement;
    const thumbContainer = document.querySelector('#thumb-container') as HTMLElement;
    const orderCreated = document.querySelector('#order-placed') as HTMLElement;
    const orderTotal = document.querySelector('#order-total') as HTMLElement;
    const orderItems = document.querySelector('#order-items') as HTMLElement;
    const shippingContainer = document.querySelector('#shipping-options-container') as HTMLElement;
    const cartPaymentContainer = document.querySelector('#cart-payment-container') as HTMLElement;
    const poCartPaymentContainer = document.querySelector('#po-cart-payment-container') as HTMLElement;
    const nonPaymentWarning = document.querySelector('#non-payment-warning') as HTMLElement;
    const poNonPaymentWarning = document.querySelector('#po-non-payment-warning') as HTMLElement;
    const cartPaymentWarnIcon = document.querySelector('#warning-icon') as HTMLElement;
    const poCartPaymentWarnIcon = document.querySelector('#po-warning-icon') as HTMLElement;
    const totalNum = document.querySelector('#balance-total') as HTMLElement;
    const poTotalNum = document.querySelector('#po-balance-total') as HTMLElement;
    const paidNum = document.querySelector('#balance-payment') as HTMLElement;
    const poPaidNum = document.querySelector('#po-balance-payment') as HTMLElement;
    const balanceNum = document.querySelector('#balance-remaining') as HTMLElement;
    const poBalanceNum = document.querySelector('#po-balance-remaining') as HTMLElement;
    const makePaymentBtn = document.querySelector('.make-payment-btn') as HTMLElement;
    const cardContainer = document.querySelector<HTMLElement>('#payment-methods-container');

    const key = G().Get('id');

    if (!key) {
        showNotFound();
        return;
    }

    axios.get<orderResp>(`/orders/${key}`).
        then(async resp => {
            const order = resp.data.order;
            const itemQuantity = (arr: models.OrderLineItem[]) => {
                let totalQuantity = 0;

                arr.forEach(lineItem => {
                    if (lineItem.lineItemModel?.product || !lineItem.lineItemModel) {
                        totalQuantity += lineItem.quantity || 0;
                    }
                });

                return totalQuantity;
            };

            tableContainer.append(await lineitems.Table(order.orderLineItems || []));
            invoiceNumberEl.innerText = String(order.invoiceNumber);
            orderTotal.innerText = DollarFormat(order.total || Big('0'));
            orderItems.innerText = String(itemQuantity(order.orderLineItems || []));
            orderCreated.innerText = DateTime.fromISO(order.created || '').toLocaleString(DateTime.DATETIME_SHORT);

            if (order.orderShippingOptions) {
                for (const shippingAddress of order.orderShippingOptions) {
                    const el = template.Clone('#shipping-template');
                    const addressEl = el.querySelector('address');
                    (addressEl as HTMLElement).classList.add('border');

                    (addressEl as HTMLElement).innerHTML = Nl2br(Escape(await Format(shippingAddress.shippingOption as models.ShippingOption)));
                    (addressEl as HTMLElement).classList.add('fs-5');

                    shippingContainer.append(addressEl as HTMLElement);
                    shippingAddress.orderShippingOptionShipments?.forEach(shipment => {
                        const el = template.Clone('.shipment-template');
                        const shipmentWrapper = el.querySelector('.shipment-wrapper') as HTMLDivElement;
                        const carrier = el.querySelector('.carrier');
                        const shipped = el.querySelector('.shipped');
                        const delivered = el.querySelector('.delivered');
                        const trackingNum = el.querySelector('a.tracking-number') as HTMLAnchorElement;
                        const trackingNumSpan = el.querySelector('span.tracking-number');
                        shipmentWrapper.classList.add('border');
                        (carrier as HTMLElement).textContent = shipment?.shipment?.carrier?.name || '';
                        if (shipment.shipment?.shipped) {
                            (shipped as HTMLElement).textContent = DateTime.fromISO(shipment.shipment?.shipped).toLocaleString(DateTime.DATETIME_SHORT);
                        } else if (shipped?.parentElement) {
                            shipped.parentElement.classList.add('d-none');
                        }
                        if (shipment.shipment?.delivered) {
                            (delivered as HTMLElement).textContent = DateTime.fromISO(shipment.shipment?.delivered).toLocaleString(DateTime.DATETIME_SHORT);
                            shipmentWrapper.classList.add('bg-primary-30');
                        } else if (delivered?.parentElement) {
                            delivered.parentElement.classList.add('d-none');
                            shipmentWrapper.classList.add('bg-secondary-30');
                        }
                        trackingNum.textContent = shipment.shipment?.trackingNumber || '';
                        if (shipment.shipment?.carrier?.link) {
                            trackingNum.href = shipment.shipment?.carrier.link.replace('%%', shipment.shipment.trackingNumber || '');
                        } else if (trackingNumSpan) {
                            trackingNum.classList.add('d-none');
                            trackingNumSpan.classList.remove('d-none');
                            trackingNumSpan.textContent = shipment.shipment?.trackingNumber || '';
                        }

                        (addressEl as HTMLElement).append(shipmentWrapper);

                        shipmentWrapper.style.fontSize = '1rem';
                    });
                }
            }

            const drawPayment = async (payment: models.PaymentOption) => {
                const paymentEl = (await DrawPaymentOption(payment)).querySelector<HTMLElement>('div');
                const cardEl = template.Clone('#card-template').firstElementChild as HTMLElement;
                const paymentContainer = cardEl.querySelector('.payment-container') as HTMLElement;
                paymentContainer?.append(paymentEl as HTMLElement);
                cardContainer?.insertBefore(cardEl, cardContainer.firstChild);
            };

            if (order.orderPayments) {
                for (const paymentMethod of order.orderPayments) {
                    await drawPayment(paymentMethod.paymentOption as models.PaymentOption);
                    const paymentAmount = document.querySelector('#payment-amount') as HTMLElement;
                    paymentAmount.innerText = DollarFormat(paymentMethod.total || '');
                }
            }

            for (const orderProof of order.orderProofs || []) {
                const proofEl = template.Clone('#proof-template');
                const imgEl = proofEl.querySelector('img') as HTMLImageElement;
                const anchorEl = proofEl.querySelector('a') as HTMLAnchorElement;

                anchorEl.href = orderProof?.proof?.main || '';
                anchorEl.dataset.caption = orderProof?.proof?.name;

                imgEl.src = orderProof?.proof?.thumbnail || '';

                thumbContainer?.append(proofEl);
            }

            const balance = Big(order.balance || 0);
            if (balance.gt('0.05')) {
                makePaymentBtn.classList.remove('d-none');
                makePaymentBtn.addEventListener('click', () =>
                    payments.PromptPayment(sess, [order.id || ''], balance, true).
                        then(() => window.location.reload()),
                );
            }

            const reorder = document.querySelector('#order-reorder') as HTMLButtonElement;
            reorder.addEventListener('click', async () => {
                buttons.Start(reorder);
                await carts.AddCartItem({ orderID: order.key ?? '' }).then(() => {
                    reorder.classList.remove('btn-primary', 'btn-success', 'text-white');
                    reorder.classList.add('btn-outline-success');

                    reorder.querySelector('i')?.classList.remove('fa-shopping-cart');
                    reorder.querySelector('i')?.classList.add('fa-check');
                }).finally(() => buttons.Stop(reorder));
            });

            const invoice = document.querySelector('#order-invoice') as HTMLAnchorElement;
            invoice.href = `${window.apiRoot}/orders/${order.id || ''}/invoice`;

            function hasPo(order: models.Order) {
                if (order.orderPayments) {
                    for (const orderPayment of order.orderPayments) {
                        if (orderPayment.paymentOption) {
                            if (orderPayment.paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                                return true;
                            }
                        }
                    }
                }
                return false;
            }

            if (!hasPo(order) && balance.gt('0.05')) {
                cartPaymentContainer.classList.remove('d-none');
                totalNum.innerText = `$${Big(order.total || '0').toFixed(2)}`;
                paidNum.innerText = `$${Big(order.paid || '0').toFixed(2)}`;
                balanceNum.innerText = `$${Big(order.balance || '0').toFixed(2)}`;
                cartPaymentContainer.classList.add('alert', 'alert-warning', 'w-75');
                cartPaymentWarnIcon.classList.remove('d-none');
                nonPaymentWarning.classList.remove('d-none');
                poCartPaymentContainer.classList.add('d-none');
            } else if (hasPo(order) && balance.gt('0.05')) {
                poCartPaymentContainer.classList.remove('d-none');
                poCartPaymentContainer.classList.add('d-flex');
                poTotalNum.innerText = `$${Big(order.total || '0').toFixed(2)}`;
                poPaidNum.innerText = `$${Big(order.paid || '0').toFixed(2)}`;
                poBalanceNum.innerText = `$${Big(order.balance || '0').toFixed(2)}`;
                poCartPaymentContainer.classList.add('alert', 'alert-success', 'w-75');
                poCartPaymentWarnIcon.classList.remove('d-none');
                poNonPaymentWarning.classList.remove('d-none');
                cartPaymentContainer.classList.remove('alert', 'alert-warning');
                cartPaymentWarnIcon.classList.add('d-none');
                nonPaymentWarning.classList.add('d-none');
                cartPaymentContainer.classList.add('d-none');
            } else {
                poCartPaymentContainer.classList.remove('alert', 'alert-warning');
                poCartPaymentWarnIcon.classList.add('d-none');
                poNonPaymentWarning.classList.add('d-none');
                poCartPaymentContainer.classList.add('d-none');
                cartPaymentContainer.classList.remove('alert', 'alert-warning');
                cartPaymentWarnIcon.classList.add('d-none');
                nonPaymentWarning.classList.add('d-none');
                cartPaymentContainer.classList.add('d-none');
            }

            const spinner = document.querySelector('#spinner');
            const orderContainer = document.querySelector('#order-container');
            spinner?.classList.add('d-none');
            orderContainer?.classList.remove('d-none');
        }).
        catch(err => {
            showNotFound();
            throw err;
        });
}